/**
 * The person's language on their account (`/me.user.language`), so their
 * emails are written in it and another device opens in it.
 *
 * The device's own choice (`kb.preferences`) still decides what this screen
 * shows, offline included. A choice made while signed in is remembered as
 * pending for that person and sent with `PATCH /me/preferences`; when that
 * fails (offline, server down) it is sent again after the next fresh `/me`.
 * After a fresh `/me` with nothing pending, the device adopts the account's
 * language when one was chosen. A choice made before signing in stays on the
 * device only: the account's own choice wins at sign-in.
 */
import { setLanguage } from '@/i18n';
import { api } from '@/lib/api/client';
import { isViewAs } from '@/lib/auth/view-as';

import { getPreferences, LANGUAGE_CODES, type LanguageCode } from './preferences';
import { readJson, removeStorage, writeJson } from './storage';

const PENDING_KEY = 'kb.language.pending';

type Pending = { user: string; language: LanguageCode };

function isLanguage(value: unknown): value is LanguageCode {
  return typeof value === 'string' && (LANGUAGE_CODES as readonly string[]).includes(value);
}

function readPending(): Pending | null {
  const value = readJson(PENDING_KEY) as Partial<Pending> | null;
  if (!value || typeof value.user !== 'string' || !isLanguage(value.language)) return null;
  return { user: value.user, language: value.language };
}

/** Send a pending choice of this person's; true when nothing is left to send. */
export async function flushLanguagePreference(userId: string): Promise<boolean> {
  const pending = readPending();
  if (!pending) return true;
  if (pending.user !== userId) {
    // Someone else's choice on a shared device: never sent for this person.
    removeStorage(PENDING_KEY);
    return true;
  }
  try {
    await api.patch('/me/preferences', { language: pending.language });
  } catch {
    return false; // offline or refused: kept, sent after the next fresh /me
  }
  if (readPending()?.language === pending.language) removeStorage(PENDING_KEY);
  return true;
}

/** The language picker, signed in: keep the choice on the account too. */
export function saveLanguagePreference(userId: string, language: LanguageCode): Promise<boolean> {
  // Staff viewing as someone: this device's language only, never the person's account.
  if (isViewAs()) return Promise.resolve(true);
  writeJson(PENDING_KEY, { user: userId, language } satisfies Pending);
  return flushLanguagePreference(userId);
}

/**
 * After a fresh `/me`: a choice still pending goes up (the person's latest
 * word); otherwise the device follows the account's language when set.
 */
export async function reconcileLanguage(me: {
  user: { id: string; language?: string | null };
}): Promise<void> {
  const pending = readPending();
  if (pending && pending.user === me.user.id) {
    await flushLanguagePreference(me.user.id);
    return;
  }
  if (pending) removeStorage(PENDING_KEY);
  const chosen = me.user.language;
  if (isLanguage(chosen) && chosen !== getPreferences().language) await setLanguage(chosen);
}

/** Test helper. */
export function resetLanguagePreferenceForTests(): void {
  removeStorage(PENDING_KEY);
}
